<template>
    <div key="settings" class="flex flex-col min-w-[75%] self-center">
        <the-main-header name="Settings" illu-name="settings" />
        <section
            class="flex flex-col p-5 sm:p-7 md:p-10 xl:p-12 bg-section-bg-500 dark:bg-section-bg-dark-500 sm:rounded-2xl shadow-big-light"
        >
            <header
                class="flex border-b pb-2 border-divider-bg-500 dark:border-divider-bg-dark-500 border-opacity-40 dark:border-opacity-40"
            >
                <h2 class="font-title font-semibold text-2xl">Content</h2>
            </header>
            <ul class="flex mt-4">
                <li class="flex flex-grow">
                    <radio-group
                        v-model="adultContent"
                        class="space-y-2 xl:max-w-[75%] 2xl:max-w-[50%]"
                    >
                        <radio-group-label class="text-base font-medium"
                            >Adult content display</radio-group-label
                        >
                        <div
                            class="rounded-lg ring-1 ring-opacity-40 ring-divider-bg-500 dark:ring-divider-bg-dark-500 dark:ring-opacity-40"
                        >
                            <radio-group-option
                                as="template"
                                v-for="adultContentOption in adultContentOptions"
                                :key="adultContentOption.name"
                                :value="adultContentOption.value"
                                v-slot="{ checked }"
                                @click="updateAdult"
                                class="first:rounded-t-lg last:rounded-b-lg first:border-t-0"
                            >
                                <div
                                    class="relative flex px-4 py-3 lg:px-5 lg:py-4 cursor-pointer border-t border-opacity-40 border-divider-bg-500 dark:border-divider-bg-dark-500 dark:border-opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-brand-200 transition-colors duration-300 ease-in-out"
                                    :class="[
                                        checked
                                            ? 'bg-brand-500 text-app-font-dark-500 hover:bg-opacity-90'
                                            : 'bg-main-bg-500 dark:bg-main-bg-dark-500 hover:bg-opacity-40',
                                    ]"
                                >
                                    <div
                                        class="flex items-center justify-between w-full"
                                    >
                                        <div class="flex items-center text-sm">
                                            <div class="text-sm">
                                                <radio-group-label
                                                    as="p"
                                                    :class="
                                                        checked
                                                            ? 'text-app-font-dark-500'
                                                            : 'text-app-font-500 dark:text-app-font-dark-500'
                                                    "
                                                    class="text-base font-medium"
                                                >
                                                    {{
                                                        adultContentOption.name
                                                    }}
                                                </radio-group-label>
                                                <radio-group-description
                                                    as="span"
                                                    :class="
                                                        checked
                                                            ? 'text-brand-100'
                                                            : 'text-app-font-light-grey-500'
                                                    "
                                                    class="inline"
                                                >
                                                    <span>{{
                                                        adultContentOption.description
                                                    }}</span>
                                                </radio-group-description>
                                            </div>
                                        </div>
                                        <div
                                            v-show="checked"
                                            class="flex-shrink-0 text-white"
                                        >
                                            <svg
                                                class="w-6 h-6"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                            >
                                                <circle
                                                    cx="12"
                                                    cy="12"
                                                    r="12"
                                                    fill="#fff"
                                                    fill-opacity="0.2"
                                                />
                                                <path
                                                    d="M7 13l3 3 7-7"
                                                    stroke="#fff"
                                                    stroke-width="1.5"
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                />
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                            </radio-group-option>
                        </div>
                    </radio-group>
                </li>
            </ul>
        </section>
    </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue'
import TheMainHeader from '@/common/components/TheMainHeader.vue'
import {
    RadioGroup,
    RadioGroupLabel,
    RadioGroupDescription,
    RadioGroupOption,
} from '@headlessui/vue'
import { useMutation, useQuery, useResult } from '@vue/apollo-composable'
import {
    GET_USER_QUERY,
    UPDATE_ADULT_MUTATION,
} from '@/services/apollo/requests'
import { GraphQLUserData } from '@/services/apollo/types'

enum AdultContentSetting {
    ADULT_ALLOWED = 'ADULT_ALLOWED',
    CONTENT_RESTRICTED = 'CONTENT_RESTRICTED',
    MATURE_ALLOWED = 'MATURE_ALLOWED',
}

const adultContentOptions = [
    {
        name: 'Restricted',
        description:
            'Sources will not have any sort of adult content available. Each title within is assumed safe for all audiences.',
        value: AdultContentSetting.CONTENT_RESTRICTED,
    },
    {
        name: 'Mature',
        description:
            'Shows content and sources which may not be guaranteed to contain content safe for all audiences. This is what we recommend most users use.',
        value: AdultContentSetting.MATURE_ALLOWED,
    },
    {
        name: 'Adult',
        description:
            'No content filtering. Additionally, this unlocks a setting menu inside of the Paperback application allowing you to change this setting on your device.',
        value: AdultContentSetting.ADULT_ALLOWED,
    },
]

export default defineComponent({
    name: 'Settings',
    components: {
        TheMainHeader,
        RadioGroup,
        RadioGroupLabel,
        RadioGroupDescription,
        RadioGroupOption,
    },

    setup() {
        const { result, onResult } = useQuery(GET_USER_QUERY)
        const { mutate: updateAdult } = useMutation(
            UPDATE_ADULT_MUTATION,
            () => ({
                variables: { status: adultContent.value },
                update: (cache) => {
                    const data = JSON.parse(
                        JSON.stringify(
                            cache.readQuery({
                                query: GET_USER_QUERY,
                            })
                        )
                    )
                    data.getUserData.adultEnabled = adultContent.value
                    cache.writeQuery({ query: GET_USER_QUERY, data })
                },
            })
        )

        const adultContentGQL = useResult(
            result,
            null,
            (data: GraphQLUserData) => data.getUserData?.adultEnabled
        )

        const adultContent = ref('')

        onResult(
            () =>
                (adultContent.value = (adultContentGQL.value as unknown) as string)
        )

        onMounted(() => {
            adultContent.value = (adultContentGQL.value as unknown) as string
        })

        return {
            adultContent,
            adultContentOptions,
            updateAdult,
        }
    },
})
</script>
